<script setup lang="ts">
import AppButton from '@/components/ui/AppButton.vue'
import AppointmentDateTimePicker from '@/components/ui/AppointmentDateTimePicker.vue'
import { getRemoteDiagnose, rescheduleRemoteDiagnose } from '@/services/remote-diagnose'
import { pinia } from '@/stores'
import { useRouterStore } from '@/stores/router'
import { onMounted, ref } from 'vue'
import Loading from '@/components/ui/Loading.vue'
import type { RemoteDiagnoseEvent } from '@/types/remote-diagnose'
import { storeToRefs } from 'pinia'
import { useRemoteDiagnose } from '@/stores/remote-diagnose'
import { useLang } from '@/composables/use-lang'
import RescheduleContent from '@/lang/reschedule-appointment.json'
import { validateDateAndTime } from '@/utils/limit-date'
import { cleanQueryString } from '@/utils/router'

type Error = {
  status: boolean
  message: string
  hideForm: boolean
}

type LoadingState = {
  appointment: boolean
  update: boolean
}

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  }
})

const { current, push } = useRouterStore(pinia)
const remoteDiagnoseStore = useRemoteDiagnose(pinia)
const { state } = storeToRefs(remoteDiagnoseStore)

const langContent = useLang(RescheduleContent)

const remote_diagnose = ref<RemoteDiagnoseEvent | null>()
const error = ref<Error>({
  status: false,
  message: '',
  hideForm: false
})
const loading = ref<LoadingState>({
  appointment: false,
  update: false
})
const loadingPage = ref<boolean>(true)
const appointmentDatePicker = ref<typeof AppointmentDateTimePicker>()
const currentDate = ref<string>('')

const handleUpdateDate = (selectedDate: string) => {
  remote_diagnose.value!.time = ''
  remote_diagnose.value!.date = selectedDate
}

const validateInputs = () => {
  let hasError = false

  if (!remote_diagnose.value!.date) {
    appointmentDatePicker.value?.setDateError()
    hasError = true
  }
  if (!remote_diagnose.value!.time) {
    appointmentDatePicker.value?.setTimeError()
    hasError = true
  }
  return !hasError
}

const onSubmit = async () => {
  loading.value.update = true

  if (!validateInputs()) {
    loading.value.update = false
    return
  }

  state.value.date = remote_diagnose.value!.date
  state.value.time = remote_diagnose.value!.time

  const formData = {
    payment_id: current.params.paymentId,
    date: remote_diagnose.value!.date,
    time: remote_diagnose.value!.time,
    name: remote_diagnose.value!.project_name
  }

  const result = await rescheduleRemoteDiagnose(formData, props.aws_url)

  if (result.data) {
    loading.value.update = false
    push('schedule-appointment-result')
    return null
  }

  error.value.status = true
  error.value.message = langContent.errors.server_error
  loading.value.update = false
}

const pageLoaded = () => {
  loadingPage.value = false
}

const redirectToHome = () => {
  cleanQueryString()
  push('home')
}

onMounted(async () => {
  if (current.params.reschedule !== '1' || !current.params.paymentId) {
    push('home')
    return null
  }

  const result = await getRemoteDiagnose(current.params.paymentId, props.aws_url)

  if (result.error || !validateDateAndTime(result.date, result.time)) {
    error.value.status = true
    error.value.message = langContent.errors.invalid_date
    error.value.hideForm = true
    loading.value.appointment = false
    loadingPage.value = false
    return null
  }

  currentDate.value = result.date
  remote_diagnose.value = { ...result, time: '', date: '' }
  loading.value.appointment = false
})
</script>

<template>
  <div v-if="langContent">
    <Loading v-if="loadingPage" />
    <div v-if="!loading.appointment" class="text-center">
      <hgroup class="mb-10">
        <hgroup class="mb-10">
          <h2>{{ langContent.reschedule_appointment }}</h2>
          <p>{{ langContent.reschedule_instructions }}</p>
        </hgroup>
      </hgroup>
      <form
        v-if="!error.hideForm && remote_diagnose"
        @submit.prevent="onSubmit"
        class="flex flex-col items-center"
      >
        <appointment-date-time-picker
          ref="appointmentDatePicker"
          :aws_url="aws_url"
          :minDate="currentDate"
          v-model="remote_diagnose.time"
          @updateDate="handleUpdateDate"
          @pageLoaded="pageLoaded"
        ></appointment-date-time-picker>

        <app-button type="submit" :disabled="loading.update" :loading="loading.update">{{
          langContent.continue
        }}</app-button>
      </form>

      <div class="mt-10" v-if="error.status">
        <p class="text-sweet-brown">
          {{ error.message }}
        </p>
        <app-button @click="redirectToHome" class="mt-5 mx-auto">{{
          langContent.redirect
        }}</app-button>
      </div>
    </div>
  </div>
</template>
